import { Person } from "@mui/icons-material";
import { Box, Typography, Card, CardContent } from "@mui/material";
import React from "react";

import { useCelleraTheme } from "../../../../theme/useCelleraTheme";

export interface PatientInfoData {
  personalInfo: {
    dateOfBirth: string;
    phone: string;
    address: string;
    emergencyContact: string;
  };
  medicalInfo: {
    bloodType: string;
    allergies: string;
    conditions: string;
    insuranceStatus: string;
  };
  billingInfo: {
    billingStatus: string;
    payment: string;
    paymentType: string;
    paymentProfile: string;
  };
}

interface PatientInfoProps {
  patientInfo: PatientInfoData;
}

const PatientInfo: React.FC<PatientInfoProps> = ({ patientInfo }) => {
  const { colors } = useCelleraTheme();

  const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ mb: 1 }}>
      <Typography
        className="font-inter font-medium text-xs leading-4 tracking-wider"
        sx={{
          color: colors.text.primary,
        }}
      >
        {label}: {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
      {/* Personal Information Card */}
      <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
        <Card
          className="rounded-xl border border-solid"
          sx={{
            height: "100%",
            borderColor: "#C6C5D5",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Person
                sx={{
                  mr: 1,
                  fontSize: 20,
                  color: colors.text.primary,
                }}
              />
              <Typography
                className="font-inter font-normal pb-2 flex-1 border-b border-gray-200"
                sx={{
                  color: colors.text.primary,
                  borderBottom: `1px solid ${colors.border.light}`,
                }}
              >
                Personal Information
              </Typography>
            </Box>
            <Box sx={{ mt: 1, ml: 3 }}>
              <InfoItem
                label="Date of Birth"
                value={patientInfo.personalInfo.dateOfBirth}
              />
              <InfoItem label="Phone" value={patientInfo.personalInfo.phone} />
              <InfoItem
                label="Address"
                value={patientInfo.personalInfo.address}
              />
              <InfoItem
                label="Emergency Contact"
                value={patientInfo.personalInfo.emergencyContact}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Medical Information Card */}
      <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
        <Card
          className="rounded-xl border border-solid"
          sx={{
            height: "100%",
            borderColor: "#C6C5D5",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Person
                sx={{
                  mr: 1,
                  fontSize: 20,
                  color: colors.text.primary,
                }}
              />
              <Typography
                className="font-inter pb-2 flex-1 border-b border-gray-200"
                sx={{
                  color: colors.text.primary,
                  borderBottom: `1px solid ${colors.border.light}`,
                }}
              >
                Medical Information
              </Typography>
            </Box>
            <Box sx={{ mt: 1, ml: 3 }}>
              <InfoItem
                label="Blood Type"
                value={patientInfo.medicalInfo.bloodType}
              />
              <InfoItem
                label="Allergies"
                value={patientInfo.medicalInfo.allergies}
              />
              <InfoItem
                label="Conditions"
                value={patientInfo.medicalInfo.conditions}
              />
              <InfoItem
                label="Insurance Status"
                value={patientInfo.medicalInfo.insuranceStatus}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Billing Information Card */}
      <Box sx={{ flex: "1 1 250px", minWidth: 250 }}>
        <Card
          className="rounded-xl border border-solid"
          sx={{
            height: "100%",
            borderColor: "#C6C5D5",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Person
                sx={{
                  color: colors.primary.main,
                  mr: 1,
                  fontSize: 20,
                }}
              />
              <Typography
                className="font-inter font-normal pb-2 flex-1 border-b border-gray-200"
                sx={{
                  color: colors.text.primary,
                  borderBottom: `1px solid ${colors.border.light}`,
                }}
              >
                Billing Information
              </Typography>
            </Box>
            <Box sx={{ mt: 1, ml: 3 }}>
              <InfoItem
                label="Billing Status"
                value={patientInfo.billingInfo.billingStatus}
              />
              <InfoItem
                label="Payment"
                value={patientInfo.billingInfo.payment}
              />
              <InfoItem
                label="Payment Type"
                value={patientInfo.billingInfo.paymentType}
              />
              <InfoItem
                label="Payment Profile"
                value={patientInfo.billingInfo.paymentProfile}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PatientInfo;
